
import _ from 'lodash';
import moment from 'moment';

function computeInterventionStyle(days, intervention) {
  const nbDays = days.length;
  const daysDuration = intervention.plannedDaysDuration;
  const plannedStartAtMoment = moment(intervention.plannedStartAt, `YYYYMMDDHHmm`);
  const plannedStartAt = plannedStartAtMoment.format(`YYYYMMDD`);
  const plannedEndAt = plannedStartAtMoment.clone().add(daysDuration - 1, `days`).format(`YYYYMMDD`);
  const leftIndex = _.findIndex(days, { iso: plannedStartAt });
  let isLeftTruncated = false;
  let isRightTruncated = false;
  let rightIndex = _.findIndex(days, { iso: plannedEndAt });
  if (rightIndex === -1) {
    rightIndex = nbDays + 1;
  } else {
    rightIndex += 1;
  }
  if (rightIndex > nbDays) {
    rightIndex = nbDays;
    isRightTruncated = true;
  }
  const top = 20 + intervention.$rowIndex * 32;
  let left = (leftIndex / nbDays) * 100;
  let width = ((rightIndex - leftIndex) / nbDays) * 100;
  if (left < 0) {
    width -= Math.abs(left);
    left = 0;
    isLeftTruncated = true;
  }
  const style = {
    isLeftTruncated,
    isRightTruncated,
    top: `${top}px`,
    left: `${left}%`,
    width: `${width}%`,
    $top: top,
    $left: left,
    $width: width,
  };
  return {
    ...intervention,
    style,
  };
}

const testRowIndexForIntervention = (datesRows, rowIndex, intervention) => {
  const currentDatesRows = _.clone(datesRows);
  let hasErrors = false;
  for (let i = 0; i < intervention.plannedDaysDuration; i += 1) {
    const date = moment(intervention.plannedStartAt, `YYYYMMDDHHmm`).add(i, `days`).format(`YYYYMMDD`);
    if (!currentDatesRows[date]) {
      currentDatesRows[date] = [];
    }
    if (currentDatesRows[date][rowIndex] === true) {
      hasErrors = true;
    }
  }
  return !hasErrors;
};

const findRowIndexForIntervention = (datesRows, intervention) => {
  let rowIndexCandidate = -1;
  let hasFoundRowIndex = false;
  while (!hasFoundRowIndex) {
    rowIndexCandidate += 1;
    hasFoundRowIndex = testRowIndexForIntervention(datesRows, rowIndexCandidate, intervention);
  }

  return rowIndexCandidate;
};

const namespaced = true;

const state = {
  viewTimeMode: `week`,
  viewRowsMode: `users`,
  firstChoosenDay: moment(),
  users: [],
  searchString: ``,
  interventions: [],
};

const mutations = {
  SET_VIEW_TIME_MODE(state, viewTimeMode) {
    state.viewTimeMode = viewTimeMode;
  },
  SET_VIEW_ROWS_MODE(state, viewRowsMode) {
    state.viewRowsMode = viewRowsMode;
  },
  SET_FIRST_CHOOSEN_DAY(state, firstChoosenDay) {
    state.firstChoosenDay = firstChoosenDay;
  },
  SET_USERS(state, users) {
    state.users = users;
  },
  SET_SEARCH_STRING(state, searchString) {
    state.searchString = searchString;
  },
  SET_INTERVENTIONS(state, interventions) {
    state.interventions = interventions;
  },
};

const actions = {
  async fetchUsers({ commit, dispatch, getters }) {
    const users = await dispatch(`model/user/fetchForClient`, getters.client.id, { root: true });
    commit(`SET_USERS`, users);

    return users;
  },
  async fetchInterventions({ commit, dispatch, getters }) {
    const interventions = await dispatch(`model/intervention/fetchList`, {
      queryParams: {
        from: getters.firstDay.moment.format(`YYYYMMDD`),
        to: getters.lastDay.moment.format(`YYYYMMDD`),
      },
    }, { root: true });
    commit(`SET_INTERVENTIONS`, interventions);

    return interventions;
  },
  updateInterventionLocally({ state, commit }, intervention) {
    const interventions = _.clone(state.interventions);
    const interventionIndex = _.findIndex(interventions, { id: intervention.id });

    interventions[interventionIndex] = intervention;
    commit(`SET_INTERVENTIONS`, interventions);
  },
};

const getters = {
  flavoredInterventions: state =>
    state.interventions.map(intervention => ({
      ...intervention,
      $searchString: _.toLower(`
        ${intervention.title}
        ${intervention.description}
        ${intervention.client?.name || ``}
        ${intervention.site?.name || ``}
        ${intervention.site?.address || ``}
        ${intervention.site?.postalCode || ``}
        ${intervention.site?.city || ``}
        ${intervention.assignee?.firstname || ``}
        ${intervention.assignee?.lastname || ``}
        ${intervention.assignee?.email || ``}
      `).replace(/[\r\n]/g, ` `),
    })),
  interventions: (state, getters) => {
    let interventions = getters.flavoredInterventions;
    const searchString = state.searchString || ``;
    if (_.trim(searchString) !== ``) {
      searchString.split(` `).forEach(searchStringPart => {
        searchStringPart = _.toLower(searchStringPart);
        interventions = _.filter(interventions, intervention =>
          intervention.$searchString.indexOf(searchStringPart) !== -1
        );
      });
    }
    return interventions;
  },
  client(state, getters, rootState) {
    const { company: client } = rootState[`model/client`];

    return client;
  },
  nbDays(state) {
    if (state.viewTimeMode === `month`) {
      const startOfMonthMoment = state.firstChoosenDay.clone().startOf(`month`);
      const endOfMonthMoment = state.firstChoosenDay.clone().endOf(`month`);
      return endOfMonthMoment.diff(startOfMonthMoment, `days`) + 1;
    }

    return 7;
  },
  days(state, getters) {
    const { nbDays } = getters;
    return _.times(nbDays, i => {
      const day = state.firstChoosenDay.clone().add(i, `days`);
      let header = _.upperFirst(day.format(`ddd DD/MM`));
      const weekDay = day.isoWeekday();
      const isWeekend = [6, 7].includes(weekDay);
      if (state.viewTimeMode === `month`) {
        header = day.format(`DD`);
      }
      return {
        iso: day.format(`YYYYMMDD`),
        moment: day,
        $header: header,
        $isWeekend: isWeekend,
        $weekDay: weekDay,
        $week: day.week(),
      };
    });
  },
  weekNumber(state, getters) {
    return parseInt(getters.firstDay.moment.format(`W`), 10);
  },
  firstDay(state, getters) {
    return _.head(getters.days);
  },
  lastDay(state, getters) {
    return _.last(getters.days);
  },
  usersWithInterventions(state, getters) {
    const users = state.users.map(user => {
      const datesRows = {};
      const interventions = _.chain(getters.interventions)
        .filter({ Assignee_id: user.id })
        .sortBy([`plannedStartAt`, `plannedEndAt`])
        .map(intervention => {
          const rowIndex = findRowIndexForIntervention(datesRows, intervention);
          const firstDayMoment = getters.firstDay.moment;
          const lastDayMoment = getters.lastDay.moment;
          let startsInPeriod = true;
          let endsInPeriod = true;
          for (let i = 0; i < intervention.plannedDaysDuration; i += 1) {
            const dateMoment = moment(intervention.plannedStartAt, `YYYYMMDDHHmm`).add(i, `days`);
            if (i === 0) {
              startsInPeriod = dateMoment.isSameOrAfter(firstDayMoment);
            }
            endsInPeriod = dateMoment.isSameOrBefore(lastDayMoment);
            const date = dateMoment.format(`YYYYMMDD`);
            if (!datesRows[date]) {
              datesRows[date] = [];
            }

            datesRows[date][rowIndex] = true;
          }

          const $isSelfIntervention = intervention.Maker_id === intervention.Client_id;
          let $label = intervention.title;
          if (!$isSelfIntervention) {
            $label = `${intervention.client.name} - ${$label}`;
          }
          return {
            ...intervention,
            $isSelfIntervention,
            $label,
            $rowIndex: rowIndex,
            $startsInPeriod: startsInPeriod,
            $endsInPeriod: endsInPeriod,
          };
        })
        .map(intervention => computeInterventionStyle(getters.days, intervention))
        .value();

      const nbRows = _.chain(datesRows)
        .map(dateRows => dateRows.length)
        .max()
        .value();

      let height = 15 + 32 * (nbRows || 0);
      if (height < 45) {
        height = 45;
      }

      return {
        ...user,
        $rowLabel: `${user.firstname} ${user.lastname}`,
        style: {
          height: `${height}px`,
        },
        interventions,
      };
    });
    return users;
  },
  clientsWithInterventions(state, getters) {
    const clients = [];
    getters.interventions.forEach(intervention => {
      let client = _.find(clients, { id: intervention.Client_id });
      if (!client) {
        client = _.clone(intervention.client);
        clients.push(client);
      }
      if (!client.interventions) {
        client.interventions = [];
      }

      client.interventions.push(intervention);
    });

    const clientsWithInterventions = clients.map(client => {
      const datesRows = {};
      const interventions = _.chain(client.interventions)
        .sortBy([`plannedStartAt`, `plannedEndAt`])
        .map(intervention => {
          const rowIndex = findRowIndexForIntervention(datesRows, intervention);
          const firstDayMoment = getters.firstDay.moment;
          const lastDayMoment = getters.lastDay.moment;
          let startsInPeriod = true;
          let endsInPeriod = true;
          for (let i = 0; i < intervention.plannedDaysDuration; i += 1) {
            const dateMoment = moment(intervention.plannedStartAt, `YYYYMMDDHHmm`).add(i, `days`);
            if (i === 0) {
              startsInPeriod = dateMoment.isSameOrAfter(firstDayMoment);
            }
            endsInPeriod = dateMoment.isSameOrBefore(lastDayMoment);
            const date = moment(intervention.plannedStartAt, `YYYYMMDDHHmm`).add(i, `days`).format(`YYYYMMDD`);
            if (!datesRows[date]) {
              datesRows[date] = [];
            }

            datesRows[date][rowIndex] = true;
          }

          const $isSelfIntervention = intervention.Maker_id === intervention.Client_id;
          let $label = ` - ${intervention.title}`;
          if (intervention.assignee) {
            $label = `${intervention.assignee.firstname} ${intervention.assignee.lastname}${$label}`;
          } else {
            $label = `N/A${$label}`;
          }
          return {
            ...intervention,
            $isSelfIntervention,
            $label,
            $rowIndex: rowIndex,
            $startsInPeriod: startsInPeriod,
            $endsInPeriod: endsInPeriod,
          };
        })
        .map(intervention => computeInterventionStyle(getters.days, intervention))
        .value();

      const nbRows = _.chain(datesRows)
        .map(dateRows => dateRows.length)
        .max()
        .value();

      let height = 15 + 32 * (nbRows || 0);
      if (height < 45) {
        height = 45;
      }

      return {
        ...client,
        $rowLabel: client.name,
        style: {
          height: `${height}px`,
        },
        interventions,
      };
    });

    return _.sortBy(clientsWithInterventions, (client) => {
      const minLeft = _.chain(client.interventions).map(intervention => intervention.style.$left).min().value();

      return minLeft;
    });
  },
};

export default {
  namespaced, state, mutations, actions, getters,
};
